/** Title tags for the platform and endpoint pages.
 *
 *  `Zhihu API - AnyAPI` was 18 characters and said nothing a searcher could act
 *  on. That page ranks at average position 10.0 on 201 impressions and earns
 *  **zero** clicks; site-wide CTR is 0.63%. A title that names what the API
 *  actually returns is the whole fix, and it has to come from the catalog rather
 *  than 69 hand-written strings, or it becomes a second source of truth for
 *  catalog data and rots the next time a SKU moves.
 */

import { TASK_FAMILY_LABELS } from '../../data/platformLabels';
import { platformKeyForRoute } from '../catalogPlatform';
import { m } from '../../paraglide/messages';
import { getLocale } from '../../paraglide/runtime';
import type { DisplayPrice } from '../pricing';
import { list, titleRate } from './answerParts';

/** Pluralize one lower-case English noun. Covers the shapes catalog actions
 *  actually take ("search" -> "searches", "profile" -> "profiles"); anything
 *  already plural is left alone so "backlinks" never becomes "backlinkss". */
export const pluralize = (word: string): string => {
  // Already plural has to win first, or "backlinks" becomes "backlinkses". A
  // trailing "ss" is the exception: that is a singular ending, not a plural.
  if (word.endsWith('s') && !word.endsWith('ss')) return word;
  if (/(?:s|x|z|ch|sh)$/.test(word)) return `${word}es`;
  if (/[^aeiou]y$/.test(word)) return `${word.slice(0, -1)}ies`;
  return `${word}s`;
};

/** Actions that name an operation rather than a result.
 *
 *  The catalog names most endpoints after what they return ("profile", "job",
 *  "review") and some after what they do ("find", "verify", "enrich", "crawl"),
 *  and only the first kind survives the `scrape {plural}` frame. The second kind
 *  produced "Email Tools API - scrape finds and verifies" and "eBay API - ...
 *  and product fulls".
 *
 *  Every entry here is one a live platform actually reaches. `crawl`, `map`, and
 *  `scrape` look like they belong and do not: the `web` platform is one of the
 *  nine with authored endpoints (`src/data/web.ts`), so its actions arrive as
 *  "Scrape page" and "Map site" and inflect on `page` and `site` instead.
 *
 *  Keyed on the head word `titleForm` would otherwise inflect, so the single
 *  `enrich` entry already covers `person enrich`, `organization enrich`, and any
 *  `*_enrich` SKU added later - the table is linguistic data about a word, not a
 *  per-platform override, so it does not become a second source of truth for the
 *  catalog. A mapped value replaces the head; an empty value means the action
 *  cannot name a page, so the title moves on to the platform's next endpoint and
 *  falls back to a bare `{name} API` only when a platform has nothing else.
 */
const TITLE_HEADS: Record<string, string> = {
  find: '',
  finder: '',
  full: '',
  verify: '',
  // Results rather than operations, but ones `pluralize` gets wrong: "enriches"
  // is not a word, and neither "research" nor "nutrition" takes a plural.
  enrich: 'enrichments',
  nutrition: 'nutrition',
  research: 'research',
};

/** The phrase an action contributes to a title, or `''` when it names an
 *  operation and so cannot describe the page at all. */
const titleForm = (action: string): string => {
  const parts = action.trim().toLowerCase().split(/\s+/);
  const head = parts.pop() ?? '';
  const form = head in TITLE_HEADS ? TITLE_HEADS[head] : pluralize(head);
  return form ? [...parts, form].join(' ') : '';
};

/** Shortest actions first, fewest words before fewest characters. */
const byBrevity = (a: string, b: string): number => {
  const words = a.split(/\s+/).length - b.split(/\s+/).length;
  if (words !== 0) return words;
  if (a.length !== b.length) return a.length - b.length;
  return a.localeCompare(b);
};

const normalized = (actions: readonly string[]): string[] =>
  [...new Set(actions.map((a) => a.trim().toLowerCase()).filter(Boolean))].sort(byBrevity);

/** The same order and dedupe as `normalized`, keeping each action title's own
 *  casing: a translated title lists actions as the page titles them. */
const titled = (actions: readonly string[]): string[] => {
  const seen = new Set<string>();
  return actions
    .map((a) => a.trim())
    .filter((a) => {
      const key = a.toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => byBrevity(a.toLowerCase(), b.toLowerCase()));
};

/** The endpoints a title should name: the shortest single-word actions first,
 *  because those are the head endpoints a platform is known for ("profile",
 *  "search", "post") while the long ones are variants of them. Deterministic,
 *  so the same catalog always yields the same title. */
export const leadingActions = (actions: readonly string[], limit = 3): string[] => {
  // Dedupe on the rendered form, not the raw action, and do it after the sort so
  // the shortest spelling wins. LinkedIn ships both an `ad` and an `ads`
  // endpoint; deduping the raw strings let both through and the title read
  // "scrape ads, ads, and jobs". Filtering before `slice` also means a dropped
  // action costs the title a word rather than a slot.
  const seen = new Set<string>();
  return normalized(actions)
    .filter((action) => {
      const form = titleForm(action);
      if (!form || seen.has(form)) return false;
      seen.add(form);
      return true;
    })
    .slice(0, limit);
};

/** The action phrase a title lists. English inflects the actions into what the
 *  page returns; a translated page has no inflection table, so it lists its
 *  translated action titles as given, in the same order and number. */
const titlePhrase = (actions: readonly string[], limit: number): string =>
  getLocale() === 'en'
    ? list(leadingActions(actions, limit).map(titleForm))
    : list(titled(actions).slice(0, limit));

/** `Zhihu API - scrape answers, profiles, and questions`, falling back to the
 *  bare platform title when a platform somehow has no endpoints. */
export const platformTitle = (name: string, actions: readonly string[]): string => {
  const phrase = titlePhrase(actions, 3);
  return phrase ? m.seo_title_platform({ name, actions: phrase }) : m.seo_title_platform_bare({ name });
};

/** Platforms that are not a website with public content, so "<name> scraper"
 *  names nothing anyone searches for. Three groups: the task-first families,
 *  whose second slug component names a vendor rather than a site; the SEO
 *  vendors whose own API is the product, and whose winnable term is the bare
 *  `<name> api` (`ahrefs api` is our largest non-brand query at 814 impressions
 *  in 28 days); and the capability platforms that are a verb, not a site.
 *
 *  Keyed on the platform key rather than the route id so the `twitter` ->
 *  `x-twitter` alias cannot desynchronize this set from the catalog. */
const NOT_A_SCRAPE_TARGET: ReadonlySet<string> = new Set([
  ...Object.keys(TASK_FAMILY_LABELS),
  'ahrefs',
  'semrush',
  'seo',
  'chatgpt',
  'gemini',
  'perplexity',
  'company',
  'email',
  'job_search',
  'person',
  'social',
  'technographics',
  'web',
]);

/**
 *  `Glassdoor scraper API pricing - jobs from $5.50 /1k results`.
 *
 *  The bare `<platform> api` term is not winnable: its page one belongs to the
 *  platform's own docs. The price question is, and we already rank 5th on Google
 *  for `glassdoor api pricing` - so "API pricing" stays exactly where it was and
 *  "scraper" is inserted in front of it rather than replacing it. That is the
 *  word people actually type: Search Console's last 28 days carry `zillow
 *  scraper api` and `glassdoor scraper api` as separate queries the old title
 *  matched nowhere, and the one Apify page ranked #1 for `twitter scraper` is a
 *  plain category page, not an actor. The number comes from `seo/apiPrice.ts`,
 *  so it cannot differ from the one the page header prints.
 *
 *  The action phrase is dropped here rather than appended: the price belongs to one
 *  endpoint, and listing three of them next to one figure implies the figure covers
 *  all three.
 */
export const platformPriceTitle = (routeId: string, name: string, phrase: string): string => {
  return NOT_A_SCRAPE_TARGET.has(platformKeyForRoute(routeId))
    ? m.seo_title_price({ name, phrase })
    : m.seo_title_price_scraper({ name, phrase });
};

/** `Instagram Post comments scraper API - from $0.80 per 1,000 requests`.
 *
 *  The endpoint's own action leads, because that is the phrase the page ranks
 *  for, and the price follows for the reason `platformPriceTitle` gives: the
 *  price question is the winnable intent. The old suffix named two sibling
 *  endpoints ("- also scrape posts and embeds"), words a searcher for this one
 *  did not type. The rate is `titleRate`, the phrase the platform title and the
 *  answer passage already quote. An endpoint with no lane to price keeps the
 *  bare name rather than a made-up figure. */
export const endpointTitle = (
  routeId: string,
  name: string,
  action: string,
  price: DisplayPrice | undefined,
): string => {
  if (!price) return m.seo_title_endpoint_bare({ name, action });
  const rate = titleRate(price);
  return NOT_A_SCRAPE_TARGET.has(platformKeyForRoute(routeId))
    ? m.seo_title_endpoint_price({ name, action, rate })
    : m.seo_title_endpoint_price_scraper({ name, action, rate });
};
